import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AttachmentSectionHeader } from "./AttachmentSectionHeader";
import { ShieldAlert } from "lucide-react";

interface Props {
  projectId: string;
}

interface Doc {
  id: string;
  name: string;
  status: string;
  project_specific: boolean;
  updated_at: string;
}

export const SafetyRulesSection: React.FC<Props> = ({ projectId }) => {
  const [docs, setDocs] = useState<Doc[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("safety_rule_documents")
        .select("id, name, status, project_specific, updated_at")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true });
      if (!cancelled) setDocs((data || []) as Doc[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (!docs.length) return null;

  return (
    <section className="space-y-4">
      <AttachmentSectionHeader
        title="Skydds- och ordningsregler"
        count={docs.length}
        doc="15"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
        {docs.map((d) => (
          <Card key={d.id} className="animate-fade-in">
            <CardContent className="p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-muted p-2">
                  <ShieldAlert className="h-5 w-5 text-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="font-medium truncate">{d.name}</p>
                  <p className="text-xs text-muted-foreground">
                    Uppdaterad {new Date(d.updated_at).toLocaleDateString("sv-SE")}
                  </p>
                </div>
              </div>
              <Badge variant={d.project_specific ? "default" : "outline"}>
                {d.project_specific ? "Projektspecifik" : "Generisk"}
              </Badge>
              <Button
                className="w-full"
                variant="outline"
                onClick={() => navigate(`/skyddsregler/${projectId}`)}
              >
                Öppna
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
};

export default SafetyRulesSection;
